import { LinkIcon } from '@heroicons/react/outline'
import { Icon } from 'components/icon'
import { Tooltip } from 'components/tooltip'
import { Icons } from 'interfaces'
import React from 'react'
import { Autoplay, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import { CalendarEvent } from 'utils'
import { eventStyles } from 'utils/eventStyles/EventStyles'

const stripHtml = (html: string): string =>
	html
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/g, ' ')
		.trim()

export const renderContent = (
	event: CalendarEvent,
	variant: number,
	textsStyles: string,
	onContentClick?: () => void,
	onPushClick?: () => void,
	effectiveSlots?: number
): JSX.Element | null => {
	const hideDescription = (effectiveSlots ?? 1) >= 3
	const whoIcon = event.isContent
		? Icons.speakerNotes
		: Icons.notificationsGeneric
	const whoTextTooltip = event.isContent ? 'Content item' : 'Direct push'

	const classNameIcon = `min-w-[20px] min-[20px] max-w-[20px] max-[20px] w-[20px] h-[20px] flex-shrink-0`
	const hasMultipleAssets = event.assets && event.assets.length > 1
	const { iconColor, iconBgColor, textTitle } =
		eventStyles[event.status] || eventStyles.published

	switch (variant) {
		// Single Event
		case 1:
			if (event.has_notification) {
				return (
					<div className="flex flex-col w-full h-[147px] overflow-hidden">
						<div
							className="flex flex-col w-full p-[6px] gap-0.5 flex-1 min-h-0 overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onContentClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onContentClick?.()
							}
						>
							<div className="flex gap-1 w-full overflow-hidden">
								<Tooltip text="Content item" icon={Icons.speakerNotes}>
									<div
										className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
									>
										<Icon
											fillPath
											src={Icons.speakerNotes}
											className={`${classNameIcon} ${iconColor}`}
										/>
									</div>
								</Tooltip>
								<p
									className={`text-left font-bold truncate min-w-0 ${textTitle}`}
								>
									{event.title}
								</p>
							</div>
							{!hideDescription && event.description && (
								<p
									className={`text-left line-clamp-1 !font-normal w-full ${textsStyles}`}
								>
									{stripHtml(event.description)}
								</p>
							)}
						</div>
						<div className="border-t border-gray-100 mx-1" />
						<div
							className="flex flex-col justify-center p-[6px] gap-0.5 flex-1 min-h-0 w-full overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onPushClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onPushClick?.()
							}
						>
							<div className="flex items-center gap-1 overflow-hidden">
								<Tooltip text="Direct push" icon={Icons.notificationsGeneric}>
									<div
										className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10 flex items-center gap-0.5`}
									>
										<Icon
											fillPath
											src={Icons.notificationsGeneric}
											className={`${classNameIcon} ${iconColor}`}
										/>
										<LinkIcon className={`${classNameIcon} ${iconColor}`} />
									</div>
								</Tooltip>
								<p
									className={`text-left font-bold truncate min-w-0 ${textTitle}`}
								>
									{event.title}
								</p>
							</div>
							{!hideDescription && event.description && (
								<p
									className={`text-left line-clamp-1 !font-normal w-full ${textsStyles}`}
								>
									{stripHtml(event.description)}
								</p>
							)}
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col w-full h-[147px] overflow-hidden">
					<div
						className={`${
							event.assets && event.assets.length > 0 ? 'h-1/2' : 'hidden'
						} w-full overflow-hidden`}
					>
						{event.assets && event.assets.length > 0 && (
							<Swiper
								modules={[Navigation, Pagination, Autoplay]}
								slidesPerView={1}
								loop={hasMultipleAssets}
								autoplay={
									hasMultipleAssets
										? { delay: 3000, disableOnInteraction: false }
										: false
								}
								allowTouchMove={hasMultipleAssets}
								className="h-full w-full"
							>
								{event.assets?.map((src, index) => (
									<SwiperSlide key={index} className="h-full w-full">
										<img
											src={src}
											alt={event.title}
											className="w-full h-full object-cover"
										/>
									</SwiperSlide>
								))}
							</Swiper>
						)}
					</div>
					<div
						className={`${
							event.assets && event.assets.length > 0 ? 'h-1/2' : 'h-full'
						} p-[6px] flex flex-col items-start justify-center gap-1 w-full overflow-hidden`}
					>
						<div className="flex items-start gap-1 w-full overflow-hidden">
							{(event.isContent || event.isPush) && (
								<Tooltip text={whoTextTooltip} icon={whoIcon}>
									<div
										className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
									>
										<Icon
											fillPath
											src={whoIcon}
											className={`${classNameIcon} ${iconColor}`}
										/>
									</div>
								</Tooltip>
							)}
							<p
								className={`text-left line-clamp-2 font-bold min-w-0 ${textTitle}`}
							>
								{event.title}
							</p>
						</div>
						{!hideDescription &&
							!(event.assets && event.assets.length > 0) &&
							event.description && (
								<p
									className={`text-left line-clamp-3 !font-normal w-full ${textsStyles}`}
								>
									{stripHtml(event.description)}
								</p>
							)}
					</div>
				</div>
			)
		// 2 Events
		case 2:
			if (event.has_notification) {
				return (
					<div className="flex flex-col w-full overflow-hidden">
						<div
							className="flex items-center gap-1 p-[6px] overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onContentClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onContentClick?.()
							}
						>
							<Tooltip text="Content item" icon={Icons.speakerNotes}>
								<div
									className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
								>
									<Icon
										fillPath
										src={Icons.speakerNotes}
										className={`${classNameIcon} ${iconColor}`}
									/>
								</div>
							</Tooltip>
							<p
								className={`text-left font-bold truncate min-w-0 ${textTitle}`}
							>
								{event.title}
							</p>
						</div>
						<div className="border-t border-gray-100 mx-1" />
						<div
							className="flex items-center gap-1 p-[6px] overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onPushClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onPushClick?.()
							}
						>
							<Tooltip text="Direct push" icon={Icons.notificationsGeneric}>
								<div
									className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10 flex items-center gap-0.5`}
								>
									<Icon
										fillPath
										src={Icons.notificationsGeneric}
										className={`${classNameIcon} ${iconColor}`}
									/>
									<LinkIcon className={`${classNameIcon} ${iconColor}`} />
								</div>
							</Tooltip>
							<p
								className={`text-left font-bold truncate min-w-0 ${textTitle}`}
							>
								{event.title}
							</p>
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col justify-center p-[6px] gap-0.5 w-full overflow-hidden">
					<div className="flex items-center gap-1 overflow-hidden">
						{(event.isContent || event.isPush) && (
							<Tooltip text={whoTextTooltip} icon={whoIcon}>
								<div
									className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
								>
									<Icon
										fillPath
										src={whoIcon}
										className={`${classNameIcon} ${iconColor}`}
									/>
								</div>
							</Tooltip>
						)}
						<p className={`text-left font-bold truncate min-w-0 ${textTitle}`}>
							{event.title}
						</p>
					</div>
					{!hideDescription && event.description && (
						<p
							className={`text-left line-clamp-1 !font-normal w-full ${textsStyles}`}
						>
							{stripHtml(event.description)}
						</p>
					)}
				</div>
			)
		// 3 or more events
		default:
			if (event.has_notification) {
				return (
					<div className="flex flex-col w-full overflow-hidden">
						<div
							className="flex items-center gap-1 px-[6px] py-[3px] overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onContentClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onContentClick?.()
							}
						>
							<Tooltip text="Content item" icon={Icons.speakerNotes}>
								<div
									className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
								>
									<Icon
										fillPath
										src={Icons.speakerNotes}
										className={`${classNameIcon} ${iconColor}`}
									/>
								</div>
							</Tooltip>
							<p
								className={`truncate whitespace-nowrap font-bold min-w-0 ${textTitle}`}
							>
								{event.title}
							</p>
						</div>
						<div className="border-t border-gray-100 mx-1" />
						<div
							className="flex items-center gap-1 px-[6px] py-[3px] overflow-hidden cursor-pointer"
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation()
								onPushClick?.()
							}}
							role="button"
							tabIndex={0}
							onKeyDown={(e: React.KeyboardEvent) =>
								e.key === 'Enter' && onPushClick?.()
							}
						>
							<Tooltip text="Direct push" icon={Icons.notificationsGeneric}>
								<div
									className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10 flex items-center gap-0.5`}
								>
									<Icon
										fillPath
										src={Icons.notificationsGeneric}
										className={`${classNameIcon} ${iconColor}`}
									/>
									<LinkIcon className={`${classNameIcon} ${iconColor}`} />
								</div>
							</Tooltip>
							<p
								className={`truncate whitespace-nowrap font-bold min-w-0 ${textTitle}`}
							>
								{event.title}
							</p>
						</div>
					</div>
				)
			}
			return (
				<div className="flex p-[6px] items-center justify-start gap-1 w-full overflow-hidden">
					{(event.isContent || event.isPush) && (
						<Tooltip text={whoTextTooltip} icon={whoIcon}>
							<div
								className={`rounded-[4px] p-[3px] ${iconBgColor} bg-opacity-10`}
							>
								<Icon
									fillPath
									src={whoIcon}
									className={`${classNameIcon} ${iconColor}`}
								/>
							</div>
						</Tooltip>
					)}
					<p
						className={`truncate whitespace-nowrap font-bold min-w-0 ${textTitle}`}
					>
						{event.title}
					</p>
				</div>
			)
	}
}
